
import React from 'react';
import { CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Users, SortAsc, RefreshCw, Filter } from "lucide-react";

interface MatchingCandidatesHeaderProps {
  minMatchScore: number;
  onMinMatchScoreChange: (score: number) => void;
  totalCandidates?: number;
  filteredCandidates?: number;
  sortBy?: string;
  onSortChange?: (sort: string) => void;
  onRefresh?: () => void;
  showFilters?: boolean;
  onToggleFilters?: () => void;
}

export const MatchingCandidatesHeader: React.FC<MatchingCandidatesHeaderProps> = ({
  minMatchScore,
  onMinMatchScoreChange,
  totalCandidates = 0,
  filteredCandidates = 0,
  sortBy = 'match_score',
  onSortChange,
  onRefresh,
  showFilters = false,
  onToggleFilters
}) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Matching Candidates
          </CardTitle>
          {totalCandidates > 0 && (
            <Badge variant="outline">
              {filteredCandidates} of {totalCandidates} candidates
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onToggleFilters}
            className={showFilters ? "bg-blue-50" : ""}
          >
            <Filter className="h-4 w-4 mr-1" />
            Filters
          </Button>
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 max-w-xs">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm font-medium">Minimum Match Score</Label>
            <Badge variant="outline">{minMatchScore}%</Badge>
          </div>
          <Slider
            value={[minMatchScore]}
            onValueChange={(value) => onMinMatchScoreChange(value[0])}
            max={100}
            min={0}
            step={5}
            className="w-full"
          />
        </div>

        {onSortChange && (
          <div className="flex items-center gap-2">
            <SortAsc className="h-4 w-4 text-muted-foreground" />
            <Select value={sortBy} onValueChange={onSortChange}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="match_score">Match Score</SelectItem>
                <SelectItem value="experience">Experience</SelectItem>
                <SelectItem value="last_contact">Last Contact</SelectItem>
                <SelectItem value="created_at">Recently Added</SelectItem>
                <SelectItem value="name">Name A-Z</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    </div>
  );
};
